import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import { Building, Phone, Mail, FileDown, Edit2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard, getFullAddress, isStaff } from '../../../../helpers/global';
import { makeRESTApiRequest } from '../../../../helpers/api';
import { ENDPOINTS } from '../../../../helpers/constants';
import DirectoryAssociationModal from '../../../hoaForm/DirectoryAssociationModal';
import {
  resolveEntityId,
  resolvePreferredPhoneEmail,
} from '../../../HoaDirectory/HoaDirectoryHelper';
import { formatPhoneNumber } from '../../../hoaForm/FormattedPhoneField';

const formatPhoneDisplay = value => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const formatted = formatPhoneNumber(value);
  return formatted || String(value);
};

const fields = [
  { label: 'Name', key: 'name', icon: <Building className="icon-sm" /> },
  { label: 'Management Company', key: 'managementCompany', icon: <Building className="icon-sm" /> },
  { label: 'Ordering Platform', key: 'orderingPlatform', icon: <Globe className="icon-sm" /> },
  { label: 'Phone Number', key: 'phoneNumber', icon: <Phone className="icon-sm" /> },
  { label: 'Email', key: 'email', icon: <Mail className="icon-sm" /> }
];

const HOACard = ({ hoa, fields, service, hoaIndex, serviceCreation = false, onEditHoaSlot }) => {
  const preferredContact = useMemo(() => resolvePreferredPhoneEmail(hoa), [hoa]);
  const [phoneNumber, setPhoneNumber] = useState(preferredContact.phoneNumber);
  const [email, setEmail] = useState(preferredContact.email);
  const [downloadingHoa, setDownloadingHoa] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const role = useMemo(() => localStorage.getItem('user-role'), []);

  useEffect(() => {
    const next = resolvePreferredPhoneEmail(hoa);
    setPhoneNumber(next.phoneNumber);
    setEmail(next.email);
  }, [hoa]);

  const handleDownload = async () => {
    setDownloadingHoa(true);

    const requestBody = {
      propertyAddress: getFullAddress(service) || '',
      ownerName: service?.sellerName || service?.ownerName || '',
      buyerName: service?.buyerName || '',
      closingDate: service?.closingDate ? new Date(service.closingDate).toISOString() : new Date().toISOString(),
      // Optional fields
      hoaName: hoa?.name || ''
    };

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.GENERATE_ESTOPPEL_REQUEST_LETTER,
      method: 'POST',
      requestBody,
      responseType: 'blob'
    });

    if (error) {
      toast.error(error || 'Failed to generate estoppel request letter');
      setDownloadingHoa(false);
      return;
    }

    // Create blob URL and trigger download
    const blobUrl = URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'accurateEstoppelRequestLetter.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    toast.success('Estoppel request letter downloaded successfully!');
    setDownloadingHoa(false);
  };

  const hasHOAName = useMemo(() => {
    return hoa?.['name'] && hoa?.['name']?.length > 0;
  }, [hoa]);

  const handleEditClick = () => {
    if (serviceCreation && onEditHoaSlot) {
      onEditHoaSlot(hoaIndex);
      return;
    }

    if (!hoa?.associationId) {
      toast.error('No association linked to this HOA');
      return;
    }

    setShowEditModal(true);
  };

  const HOACardValues = { ...hoa, phoneNumber, email };
  const showEditButton = serviceCreation ? Boolean(onEditHoaSlot) : Boolean(hoa?.associationId);
  const showNoEstoppelBanner = isStaff(role) && hoa.noEstoppelNeeded;

  return (
    <div className="col-lg-4 col-md-6 col-12 mt-2">
      <div className="card bg-light h-100 d-flex flex-column justify-content-between position-relative overflow-hidden">
        {isStaff(role) && (
          <div
            className="position-absolute d-flex align-items-center"
            style={{ top: 8, right: 8, zIndex: 1 }}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-generate-estoppel-${hoa?._id}`}>
                  {hasHOAName ? 'Generate Estoppel Request Letter' : 'HOA Name is required'}
                </Tooltip>
              }
            >
              <Button
                onClick={handleDownload}
                disabled={!hasHOAName || downloadingHoa}
                variant="ghost"
                className="generate-estoppel-letter-button shadow-none bg-light"
              >
                {downloadingHoa ? <Spinner size="sm" animation="border" /> : <FileDown size={18} color="#10b981" />}
              </Button>
            </OverlayTrigger>
            {showEditButton && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-edit-association-${hoa?._id}`}>
                    {serviceCreation ? 'Edit HOA' : 'Edit Association'}
                  </Tooltip>
                }
              >
                <Button
                  onClick={handleEditClick}
                  variant="ghost"
                  style={{
                    padding: '4px 8px',
                    minWidth: 'auto',
                  }}
                  className="hover-muted shadow-none"
                >
                  <Edit2 size={14} color="#6c757d" />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        )}
        <div
          className={`d-flex flex-column flex-grow-1 pl-2 py-3`}
          style={{
            paddingRight: isStaff(role) ? '4.75rem' : '0.5rem',
            gap: 8,
          }}
        >
          {fields.map((field, fieldIndex) => {
            const { key, icon } = field;
            const value =
              key === 'phoneNumber'
                ? formatPhoneDisplay(HOACardValues[key])
                : HOACardValues[key] || 'N/A';
            return (
              <div key={fieldIndex}>
                <div className="d-flex align-items-center">
                  {icon && (
                    <span className="d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0 text-muted">
                      {icon}
                    </span>
                  )}
                  <div
                    className={`ml-1 ${field.key === 'name' ? 'font-weight-semibold largeFont' : 'text-muted'}`}
                    style={{
                      wordBreak: 'break-word',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      copyToClipboard(value);
                      toast.success('Copied to clipboard!');
                    }}
                  >
                    {value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {showNoEstoppelBanner && (
          <div
            className="w-100 text-center xsmallFont font-weight-bold"
            style={{
              background: '#fef2f2',
              color: '#b91c1c',
              borderTop: '1px solid #fecaca',
              padding: '6px 8px',
            }}
          >
            No estoppel needed
          </div>
        )}
      </div>

      {showEditModal && (
        <DirectoryAssociationModal
          mode="edit"
          associationId={resolveEntityId(hoa.associationId)}
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSaved={savedAssociation => {
            const next = resolvePreferredPhoneEmail(hoa, savedAssociation);
            setPhoneNumber(next.phoneNumber);
            setEmail(next.email);
          }}
        />
      )}
    </div>
  );
};

const HOAs = ({
  hoas = [],
  requestHoaInfo = [],
  service = {},
  serviceCreation = false,
  onEditHoaSlot,
}) => {
  const hasRequestedHoaInfo =
    Array.isArray(requestHoaInfo) && requestHoaInfo.some(h => h?.name);

  return hoas.length > 0 ? (
    <div className="mb-1">
      <div className="row">
        {hoas.map((hoa, hoaIndex) => (
          <HOACard
            key={hoa._id || hoaIndex}
            hoa={hoa}
            hoaIndex={hoaIndex}
            fields={fields}
            service={service}
            serviceCreation={serviceCreation}
            onEditHoaSlot={onEditHoaSlot}
          />
        ))}
      </div>
    </div>
  ) : (
    <Alert
      variant={'secondary'}
      style={{ borderStyle: 'dashed' }}
      className={`px-3 py-4 mt-1 bg-light text-dark text-center`}
    >
      <i className="text-muted">
        {hasRequestedHoaInfo ? 'In Review' : 'No HOA added!'}
      </i>
    </Alert>
  );
};

export default HOAs;
